import { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Scales } from "@phosphor-icons/react";
import styled from "styled-components";
import {
  border,
  brand,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../../theme/tokens";
import {
  fetchBoardAppealAvailability,
  selectBoardAppealSummary,
} from "../../../slice/BoardAppealSlice";
import { daysLeftLabel, formatDay } from "../../BoardAppeals/utils";

const Tile = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing[5]};
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-left: 6px solid
    ${(p) => (p.$urgent ? status.failed.fg : brand.primary)};
  border-radius: ${radius.lg};
  padding: ${spacing[4]} ${spacing[5]};
  margin-bottom: ${spacing[4]};

  &:hover {
    border-color: ${brand.primary};
    background: ${brand.primaryLight};
  }
`;

const Count = styled.span`
  font-size: 40px;
  line-height: 1;
  font-weight: ${typography.weight.bold};
  color: ${(p) => (p.$urgent ? status.failed.fg : text.primary)};
  min-width: 56px;
  text-align: center;
`;

const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  flex: 1;
  min-width: 0;
`;

const Heading = styled.span`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

const Line = styled.span`
  font-size: ${typography.size.base};
  color: ${(p) => (p.$urgent ? status.failed.fg : text.secondary)};
`;

const Open = styled.span`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${brand.primary};
  white-space: nowrap;
`;

/**
 * The door to Board appeal review, on the reviewer's own Home. Nobody
 * else sees it, and it stays off a colleague's read-only plate.
 */
const BoardAppealsTile = ({ readOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { available, pendingCount, urgentCount, nextDue } =
    useSelector(selectBoardAppealSummary) || {};

  useEffect(() => {
    dispatch(fetchBoardAppealAvailability());
  }, [dispatch]);

  if (!available || readOnly) return null;

  const urgent = urgentCount > 0;
  return (
    <Tile
      type="button"
      $urgent={urgent}
      data-testid="board-appeals-tile"
      onClick={() => navigate("/board-appeals")}
    >
      <Scales size={36} color={urgent ? status.failed.fg : brand.primary} />
      <Count $urgent={urgent}>{pendingCount}</Count>
      <Body>
        <Heading>
          Board appeals to decide
        </Heading>
        {pendingCount === 0 ? (
          <Line>Nothing waiting.</Line>
        ) : (
          <>
            {urgent ? (
              <Line $urgent>
                {urgentCount} due to the Court within 30 days
              </Line>
            ) : null}
            {nextDue ? (
              <Line>
                Next Notice of Appeal: {nextDue.clientName}
                {nextDue.deadline ? `, ${formatDay(nextDue.deadline)}` : ""} ·{" "}
                {daysLeftLabel(nextDue.daysLeft)}
              </Line>
            ) : null}
          </>
        )}
      </Body>
      <Open>Open reviews →</Open>
    </Tile>
  );
};

BoardAppealsTile.propTypes = {
  readOnly: PropTypes.bool,
};

export default BoardAppealsTile;
